/* eslint-disable arrow-body-style */
"use client";

import { useEffect, useMemo, useRef, useState, type FC } from "react";
import {
  Button,
  Card,
  Flex,
  Grid,
  Icon,
  IconButton,
  Ruler,
  ScrollPane,
  Select,
  type SelectChangeHandler,
  type SelectListOption,
  Text,
  TextField,
} from "@huub-web/ui";

import type { AnalysisObservationCategory } from "../../../types";
import {
  analysisSectionIllustrations,
  type AnalysisSectionIndex,
} from "../../Analysis/types/AnalysisSections";
import { Illustration } from "../../Illustration";
import { SubCategoryItem } from "./SubCategoryItem";
import { AnalysisListItem } from "./AnalysisListItem";

import {
  categoryContentContainerStyle,
  categoryContentStyle,
  categoryMenuStyle,
  observationsGridStyle,
  scrollPaneContainerStyle,
  categoryContentFlexStyle,
  scrollPaneContentStyle,
  categoryContentHeaderStyle,
  rulerStyle,
  cardWrapperStyle,
  titleContainerStyle,
  assessmentSelectStyle,
  searchFieldStyle,
  subCategoryNavStyle,
  illustrationStyle,
  filterRowStyle,
  titleRowCloseStyle,
  headerCloseStyle,
  resetIconWrapperStyle,
  resetButtonWrapperStyle,
} from "./AnalysisObservationsModal.css";
import { toHumanCase } from "@huub-web/utils";

export type AnalysisObservationsModalContentProps = {
  category: AnalysisObservationCategory;
  index: AnalysisSectionIndex;
  onClose?: () => void;
};

/**
 * Content component for the AnalysisObservationsModal
 */
export const AnalysisObservationsModalContent: FC<
  AnalysisObservationsModalContentProps
> = ({ category, index, onClose }) => {
  const scrollPaneContainerRef = useRef<HTMLDivElement>(null);
  const scrollPaneRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const subCategoryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const [activeSubCategoryID, setActiveSubCategoryID] = useState<string>(
    category.observations?.[0]?.subCategoryID || "algemeen",
  );

  const assessmentOptions: SelectListOption[] = (() => {
    const assessmentOrder = [
      "slecht",
      "matig",
      "redelijk",
      "goed",
      "uitstekend",
      "onbekend",
      "niet van toepassing",
    ];

    const uniqueAssessments = Array.from(
      new Set(category?.observations?.map((obs) => obs.assessment) || []),
    );

    const options = uniqueAssessments
      .sort((a, b) => {
        const indexA = assessmentOrder.indexOf(a);
        const indexB = assessmentOrder.indexOf(b);
        const orderA = indexA === -1 ? assessmentOrder.length : indexA;
        const orderB = indexB === -1 ? assessmentOrder.length : indexB;
        return orderA - orderB;
      })
      .map((assessment) => ({
        label: toHumanCase(assessment, "ucfirst"),
        value: assessment,
      }));

    return options;
  })();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedAssessment, setSelectedAssessment] = useState<
    string[] | undefined
  >(undefined);

  const handleSelectAssessment: SelectChangeHandler = (value) => {
    setSelectedAssessment(value as string[]);
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedAssessment(undefined);
  };

  const hasActiveFilters =
    searchQuery.trim() !== "" ||
    (selectedAssessment && selectedAssessment.length > 0);

  const handleSubCategoryItemClick = (id: string) => {
    setActiveSubCategoryID(id);
    const element = subCategoryRefs.current[id];
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const filteredObservations = (() => {
    let filtered = category.observations || [];

    // Apply search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((observation) => {
        return (
          observation.component.toLowerCase().includes(query) ||
          observation.imageObservation.toLowerCase().includes(query) ||
          observation.textObservation.toLowerCase().includes(query) ||
          observation.explanation.toLowerCase().includes(query) ||
          observation.improvementAdvice?.toLowerCase().includes(query)
        );
      });
    }

    // Apply assessment filter
    if (selectedAssessment && selectedAssessment.length > 0) {
      filtered = filtered.filter((observation) =>
        selectedAssessment.includes(observation.assessment),
      );
    }

    return filtered;
  })();

  const subCategories = useMemo(() => {
    // Build all subcategories from original observations
    const allSubCategories =
      category.observations?.reduce(
        (acc, observation) => {
          const categoryID = observation.subCategoryID || "algemeen";
          // Use subCategoryName directly if provided (preserves punctuation like "Hang- en sluitwerk")
          // Only apply toHumanCase when falling back to ID-based name
          const categoryName = observation.subCategoryName
            ? observation.subCategoryName
            : toHumanCase(categoryID.replace(/_/g, " "), "ucfirst");

          if (!acc[categoryID]) {
            acc[categoryID] = {
              name: categoryName,
              totalObservations: 0,
              filteredObservations: 0,
              observations: [],
            };
          }

          return acc;
        },
        {} as Record<
          string,
          {
            name: string;
            totalObservations: number;
            filteredObservations: number;
            observations: typeof category.observations;
          }
        >,
      ) || {};

    // Count filtered observations per subcategory
    filteredObservations?.forEach((observation) => {
      const categoryID = observation.subCategoryID || "algemeen";
      if (allSubCategories[categoryID]) {
        allSubCategories[categoryID].filteredObservations += 1;
        allSubCategories[categoryID].observations.push(observation);
      }
    });

    return allSubCategories;
  }, [category.observations, filteredObservations]);

  // Track intersection of sections to update active subcategory
  useEffect(() => {
    const scrollPaneElement = scrollPaneRef.current;
    const sectionElements = subCategoryRefs.current;

    if (!scrollPaneElement || !sectionElements) {
      return;
    }

    // Store intersection ratios for all sections
    const intersectionRatios = new Map<string, number>();

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const sectionId = entry.target.getAttribute("data-section-id");
        if (sectionId) {
          intersectionRatios.set(sectionId, entry.intersectionRatio);
        }
      });

      // Find the section with the highest intersection ratio
      let maxRatio = 0;
      let mostVisibleSection: string | null = null;

      intersectionRatios.forEach((ratio, id) => {
        if (ratio > maxRatio) {
          maxRatio = ratio;
          mostVisibleSection = id;
        }
      });

      // Update active subcategory if we found a visible section
      if (mostVisibleSection && maxRatio > 0) {
        setActiveSubCategoryID(mostVisibleSection);
      }
    };

    const observer = new IntersectionObserver(observerCallback, {
      root: scrollPaneElement,
      rootMargin: "-20% 0px -60% 0px",
      threshold: [0, 0.25, 0.5, 0.75, 1],
    });

    // Observe all section elements
    Object.entries(sectionElements).forEach(([id, element]) => {
      if (element) {
        element.setAttribute("data-section-id", id);
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [subCategories]);

  useEffect(() => {
    const scrollPaneElement = scrollPaneContainerRef.current;
    const gridElement = gridRef.current;

    if (!scrollPaneElement || !gridElement) {
      return;
    }

    const updateHeight = () => {
      const scrollPaneHeight = scrollPaneElement.clientHeight;
      gridElement.style.setProperty(
        "--scrollpane-height",
        `${scrollPaneHeight}px`,
      );
    };

    // Initial update
    updateHeight();

    // Observe size changes
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(scrollPaneElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Grid ref={gridRef} variant="none" className={observationsGridStyle}>
      <Flex direction="column" className={categoryMenuStyle} gap="xs">
        <div className={illustrationStyle}>
          <Illustration name={analysisSectionIllustrations[index]} size="xxs" />
        </div>
        <Flex gap="s" align="center" justify="space-between">
          <Text variant="displayM" bold>
            {category.name}
          </Text>
          <div className={titleRowCloseStyle}>
            <IconButton
              icon="close"
              onClick={onClose}
              variant="opaque"
              border="default"
              rounded
              size="xs"
            />
          </div>
        </Flex>
        <div className={subCategoryNavStyle}>
          <Flex as="ul" direction="column" gap="xxs">
            {Object.entries(subCategories).map(
              ([id, { name, filteredObservations }]) => (
                <SubCategoryItem
                  key={id}
                  name={name}
                  totalObservations={filteredObservations}
                  active={activeSubCategoryID === id}
                  disabled={filteredObservations === 0}
                  onClick={() => handleSubCategoryItemClick(id)}
                />
              ),
            )}
          </Flex>
        </div>
      </Flex>

      <section className={categoryContentStyle}>
        <Flex direction="column" gap="m" className={categoryContentFlexStyle}>
          <Flex className={categoryContentHeaderStyle} gap="s">
            <Flex gap="xs" className={filterRowStyle}>
              <TextField
                placeholder="Zoeken..."
                leading={<Icon icon="search" size="s" color="placeholder" />}
                value={searchQuery}
                onChange={setSearchQuery}
                className={searchFieldStyle}
              />
              <Select
                multi
                options={assessmentOptions}
                placeholder="Indruk"
                value={selectedAssessment}
                onChange={handleSelectAssessment}
                className={assessmentSelectStyle}
              />
              {hasActiveFilters && (
                <>
                  <div className={resetIconWrapperStyle}>
                    <IconButton
                      icon="close"
                      onClick={handleResetFilters}
                      variant="primary"
                      size="s"
                      aria-label="Reset filters"
                    />
                  </div>
                  <div className={resetButtonWrapperStyle}>
                    <Button size="input" onClick={handleResetFilters}>
                      Reset filters
                    </Button>
                  </div>
                </>
              )}
            </Flex>
            <div className={headerCloseStyle}>
              <IconButton
                icon="close"
                onClick={onClose}
                variant="opaque"
                border="default"
                rounded
                size="xs"
              />
            </div>
          </Flex>
          <Ruler className={rulerStyle} />
          <Flex
            ref={scrollPaneContainerRef}
            grow
            direction="column"
            gap="m"
            className={scrollPaneContainerStyle}
          >
            <ScrollPane
              ref={scrollPaneRef}
              contentClassName={scrollPaneContentStyle}
              overlapColor="backgroundTint"
              overlapSize="s"
            >
              <Flex direction="column" gap="xl">
                {filteredObservations.length === 0 ? (
                  <Card variant="tertiary" gap="s">
                    <Flex gap="s" align="center">
                      <Icon icon="search" color="placeholder" />
                      <Text variant="displayS" align="center">
                        Geen inzichten gevonden
                      </Text>
                    </Flex>
                    <Text variant="bodyM" color="placeholder">
                      Probeer je filters aan te passen
                    </Text>
                  </Card>
                ) : (
                  Object.entries(subCategories).map(
                    ([id, { name, observations }]) =>
                      observations && observations.length > 0 ? (
                        <div
                          key={id}
                          ref={(el) => {
                            subCategoryRefs.current[id] = el;
                          }}
                          className={cardWrapperStyle}
                        >
                          <Flex
                            className={titleContainerStyle}
                            align="baseline"
                            gap="s"
                          >
                            <Text variant="displayS" bold>
                              {name}
                            </Text>
                            <Text variant="bodyM" color="currentColor50">
                              {observations.length}{" "}
                              {observations.length === 1
                                ? "inzicht"
                                : "inzichten"}
                            </Text>
                          </Flex>
                          <Card
                            border="default"
                            applyPadding={false}
                            overflowHidden
                            padding="none"
                            gap="none"
                          >
                            {(() => {
                              const usedUrls = new Set<string>();
                              return observations.map((observation, index) => {
                                const refs =
                                  observation.resolvedMediaReferences?.filter(
                                    (m) => !!m.url,
                                  ) ?? [];
                                const unique = refs.find(
                                  (m) => m.url && !usedUrls.has(m.url),
                                );
                                const chosen = unique ?? refs[0];
                                if (chosen?.url) usedUrls.add(chosen.url);

                                return (
                                  <div
                                    key={index}
                                    className={categoryContentContainerStyle}
                                  >
                                    <AnalysisListItem
                                      observation={observation}
                                      preferredImageUrl={
                                        chosen?.url ?? undefined
                                      }
                                    />
                                  </div>
                                );
                              });
                            })()}
                          </Card>
                        </div>
                      ) : null,
                  )
                )}
              </Flex>
            </ScrollPane>
          </Flex>
        </Flex>
      </section>
    </Grid>
  );
};
